// ==========================
// header-cart.js
// Global cart + header logic
// ==========================

// ===== GLOBAL CART STATE =====
let cart = {
    items: [],
    subtotal: 0,
    total: 0,
    currency: 'USD'
};

// ===== LOCAL STORAGE FUNCTIONS =====
function saveCartToLocalStorage() {
    try {
        localStorage.setItem('rouxCart', JSON.stringify(cart));
        console.log('Cart saved to localStorage:', cart);
    } catch (e) {
        console.error('Error saving cart to localStorage:', e);
    }
}

function loadCartFromLocalStorage() {
    try {
        const savedCart = localStorage.getItem('rouxCart');
        if (savedCart) {
            cart = JSON.parse(savedCart);
            console.log('Cart loaded from localStorage:', cart);
            updateCartUI();
            updateCartCount();
        }
    } catch (e) {
        console.error('Error loading cart from localStorage:', e);
        cart = { items: [], subtotal: 0, total: 0, currency: 'USD' };
    }
}

// ===== UNIVERSAL PRODUCT FUNCTION =====
async function getProductData(variantId) {
    try {
        // Try Shopify product endpoint
        if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
            const response = await fetch(`${window.Shopify.routes.root}products/${variantId}.js`);
            if (response.ok) {
                return await response.json();
            }
        }

        // Try global productData
        if (window.productData) {
            return window.productData;
        }

        // Try data-product element
        const productElement = document.querySelector('[data-product]');
        if (productElement) {
            return JSON.parse(productElement.dataset.product);
        }

        console.warn('Could not fetch product data for variant:', variantId);
        return null;
    } catch (error) {
        console.error('Error fetching product data:', error);
        return null;
    }
}

// ===== CART MANAGEMENT FUNCTIONS =====
function addToCart(variantId, quantity = 1, properties = {}) {
    console.log('Adding to cart:', variantId, quantity, properties);

    // Check if variant already exists in cart
    const existingItemIndex = cart.items.findIndex(item =>
        item.variant_id == variantId &&
        JSON.stringify(item.properties) === JSON.stringify(properties)
    );

    if (existingItemIndex > -1) {
        // Update existing item quantity
        cart.items[existingItemIndex].quantity += quantity;
        console.log('Updated existing item:', cart.items[existingItemIndex]);
    } else {
        // Add new item
        const newItem = {
            variant_id: variantId,
            quantity: quantity,
            properties: properties,
            title: 'ROUX Hair Texturizer', // Default title
            price: 25.00, // Default price
            image: 'https://cdn.shopify.com/s/files/1/0688/7964/6892/files/IMG_7457.heic?v=1765029891',
            handle: 'roux-hair-texturizer',
            variant_title: 'Standard'
        };
        cart.items.push(newItem);

        // Try to fetch updated product info
        updateProductInfo(variantId, newItem);
    }

    // Update cart totals
    updateCartTotals();

    // Save to localStorage
    saveCartToLocalStorage();

    // Update UI
    updateCartCount();
    updateCartUI();

    // Sync with Shopify cart
    syncShopifyCart();

    // Open cart drawer
    openCartDrawer();

    // Return cart state for possible further use
    return cart;
}

// Enhanced addToCart function for product page integration
function addProductToCart(productData) {
    console.log('Adding product to cart:', productData);

    // Check if product already exists in cart
    const existingItemIndex = cart.items.findIndex(item =>
        item.variant_id == productData.variantId
    );

    if (existingItemIndex > -1) {
        // Update existing item quantity
        cart.items[existingItemIndex].quantity += productData.quantity;
    } else {
        // Add new item
        const newItem = {
            variant_id: productData.variantId,
            quantity: productData.quantity,
            properties: productData.properties || {},
            title: productData.title || 'ROUX Hair Texturizer',
            price: productData.price || 25.00,
            image: productData.image || 'https://cdn.shopify.com/s/files/1/0688/7964/6892/files/IMG_7457.heic?v=1765029891',
            handle: productData.handle || 'roux-hair-texturizer',
            variant_title: productData.variant || 'Standard'
        };
        cart.items.push(newItem);
    }

    // Update cart totals
    updateCartTotals();

    // Save to localStorage
    saveCartToLocalStorage();

    // Update UI
    updateCartCount();
    updateCartUI();

    // Sync with Shopify cart
    syncShopifyCart();

    // Open cart drawer
    openCartDrawer();

    return cart;
}

async function updateProductInfo(variantId, item) {
    const productData = await getProductData(variantId);
    if (productData) {
        const variant = productData.variants?.find(v => v.id == variantId);
        if (variant) {
            item.title = productData.title || item.title;
            item.price = variant.price ? variant.price / 100 : item.price;
            item.image = productData.featured_image || item.image;
            item.handle = productData.handle || item.handle;
            item.variant_title = variant.title || item.variant_title;

            updateCartTotals();
            updateCartUI();
            saveCartToLocalStorage();
        }
    }
}

function updateCartTotals() {
    cart.subtotal = cart.items.reduce((sum, item) => {
        return sum + (item.price * item.quantity);
    }, 0);

    cart.total = cart.subtotal;
}

// ===== SYNC LOCAL CART WITH SHOPIFY CART =====
async function syncShopifyCart() {
    try {
        // If no items, clear Shopify cart completely
        if (cart.items.length === 0) {
            await fetch('/cart/clear.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            return;
        }

        // Build updates object: { variant_id: quantity, ... }
        const updates = {};
        cart.items.forEach(item => {
            updates[item.variant_id] = item.quantity;
        });

        await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
        });
    } catch (error) {
        console.error('Error syncing Shopify cart:', error);
    }
}

function updateCartCount() {
    const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const cartCountElements = document.querySelectorAll('.cart-count');
    cartCountElements.forEach(element => {
        element.textContent = totalItems;
    });
}

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cart.currency,
        minimumFractionDigits: 2
    }).format(amount).replace('$', '');
}

function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[&<>"]/g, function(m) {
        if (m === '&') return '&amp;';
        if (m === '<') return '&lt;';
        if (m === '>') return '&gt;';
        if (m === '"') return '&quot;';
        return m;
    });
}

function updateCartUI() {
    const cartDrawerContent = document.getElementById('cartDrawerContent');
    const checkoutButton = document.getElementById('checkoutButton');
    const cartSubtotal = document.getElementById('cartSubtotal');
    const cartTotal = document.getElementById('cartTotal');

    if (!cartDrawerContent) return;

    cartDrawerContent.innerHTML = '';

    if (cart.items.length === 0) {
        cartDrawerContent.innerHTML = `
            <div class="cart-empty-message">
                <p>Your cart is empty</p>
                <p><small>Add some products to get started!</small></p>
            </div>
        `;
        if (checkoutButton) checkoutButton.disabled = true;
    } else {
        cart.items.forEach((item, index) => {
            const itemElement = document.createElement('div');
            itemElement.className = 'cart-item';
            itemElement.innerHTML = `
                <div class="cart-item-header">
                    <div class="cart-item-info">
                        <div class="cart-item-title">${escapeHtml(item.title)}</div>
                        ${item.variant_title && item.variant_title !== 'Default Title' && item.variant_title !== 'Standard' ?
                `<div class="cart-item-variant">${escapeHtml(item.variant_title)}</div>` : ''}
                        <div class="cart-item-price">${formatCurrency(item.price)} ${cart.currency}</div>
                    </div>
                    <button class="trash-icon" onclick="removeCartItem(${index})" aria-label="Remove item" type="button">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                        </svg>
                    </button>
                </div>
                ${item.properties && Object.keys(item.properties).length > 0 ?
                `<div class="cart-item-properties">
                        ${Object.entries(item.properties).map(([key, value]) =>
                    `<div><strong>${escapeHtml(key)}:</strong> ${escapeHtml(value)}</div>`
                ).join('')}
                    </div>` : ''
            }
                <div class="quantity-controls">
                    <button class="quantity-btn" onclick="updateCartItemQuantity(${index}, ${item.quantity - 1})" type="button">-</button>
                    <span class="quantity-display">${item.quantity}</span>
                    <button class="quantity-btn" onclick="updateCartItemQuantity(${index}, ${item.quantity + 1})" type="button">+</button>
                </div>
                <div class="cart-item-subtotal">
                    <strong>Item Total:</strong> ${formatCurrency(item.price * item.quantity)} ${cart.currency}
                </div>
            `;
            cartDrawerContent.appendChild(itemElement);
        });

        if (checkoutButton) checkoutButton.disabled = false;
    }

    if (cartSubtotal) cartSubtotal.textContent = `${formatCurrency(cart.subtotal)} ${cart.currency}`;
    if (cartTotal) cartTotal.textContent = `${formatCurrency(cart.total)} ${cart.currency}`;
}

function updateCartItemQuantity(index, newQuantity) {
    if (newQuantity < 1) {
        removeCartItem(index);
        return;
    }

    cart.items[index].quantity = newQuantity;
    updateCartTotals();
    saveCartToLocalStorage();
    updateCartCount();
    updateCartUI();

    // sync to Shopify
    syncShopifyCart();
}

function removeCartItem(index) {
    cart.items.splice(index, 1);
    updateCartTotals();
    saveCartToLocalStorage();
    updateCartCount();
    updateCartUI();

    // sync to Shopify
    syncShopifyCart();
}

function clearCart() {
    cart.items = [];
    updateCartTotals();
    saveCartToLocalStorage();
    updateCartCount();
    updateCartUI();

    // sync to Shopify (will clear server cart)
    syncShopifyCart();
}

// ===== CART DRAWER FUNCTIONS =====
function openCartDrawer() {
    const cartDrawer = document.getElementById('cartDrawer');
    const cartOverlay = document.getElementById('cartOverlay');

    updateHeights();

    if (cartDrawer) cartDrawer.classList.add('active');
    if (cartOverlay) cartOverlay.classList.add('active');

    document.documentElement.classList.add('roux-cart-open');
    document.body.classList.add('roux-cart-open');

    document.body.style.overflow = 'hidden';

    // Fix for iPhone Safari - ensure checkout button is visible
    setTimeout(() => {
        updateHeights();
        positionCheckoutButtonForMobile();
    }, 100);
}

function closeCartDrawer() {
    const cartDrawer = document.getElementById('cartDrawer');
    const cartOverlay = document.getElementById('cartOverlay');

    if (cartDrawer) cartDrawer.classList.remove('active');
    if (cartOverlay) cartOverlay.classList.remove('active');

    document.documentElement.classList.remove('roux-cart-open');
    document.body.classList.remove('roux-cart-open');

    document.body.style.overflow = '';
}

// ===== IPHONE SAFARI FIXES =====
function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function positionCheckoutButtonForMobile() {
    const checkoutButton = document.getElementById('checkoutButton');
    const cartDrawerFooter = document.querySelector('.cart-drawer-footer');

    if (!checkoutButton || !cartDrawerFooter) return;

    if (isIOS() || window.innerWidth <= 768) {
        // Add extra padding for iOS Safari URL bar
        cartDrawerFooter.style.paddingBottom = 'calc(20px + env(safe-area-inset-bottom, 20px))';
        checkoutButton.style.marginBottom = 'env(safe-area-inset-bottom, 0px)';

        // Ensure button is above any browser UI
        checkoutButton.style.zIndex = '1003';
        checkoutButton.style.position = 'relative';
    }
}

// ===== MOBILE MENU FUNCTIONS =====
function toggleMobileMenu() {
    const mobileMenu = document.getElementById('mobileMenu');
    const toggleButton = document.querySelector('.mobile-menu-toggle');

    if (mobileMenu) mobileMenu.classList.toggle('active');
    if (toggleButton) toggleButton.classList.toggle('active');
    document.body.style.overflow = mobileMenu && mobileMenu.classList.contains('active') ? 'hidden' : '';
}

// ===== CHECKOUT FUNCTION =====
async function proceedToCheckout() {
    if (cart.items.length === 0) return;

    try {
        // make sure Shopify cart matches local cart
        await syncShopifyCart();
        window.location.href = '/checkout';
    } catch (error) {
        console.error('Checkout error:', error);
        alert('There was an error proceeding to checkout. Please try again.');
    }
}

// ===== DYNAMIC HEIGHT CALCULATION =====
function updateHeights() {
    const announcementBar = document.getElementById('announcementBar');
    const header = document.querySelector('.roux-header');

    const announcementH = announcementBar?.offsetHeight || 40;
    const headerH = header?.offsetHeight || 80;
    const topOffset = announcementH + headerH;

    const viewportH =
        window.visualViewport && window.visualViewport.height
            ? window.visualViewport.height
            : window.innerHeight;

    const availableH = Math.max(viewportH - topOffset, 320);

    document.documentElement.style.setProperty('--announcement-h', announcementH + 'px');
    document.documentElement.style.setProperty('--header-h', headerH + 'px');
    document.documentElement.style.setProperty('--cart-top-offset', topOffset + 'px');
    document.documentElement.style.setProperty('--cart-height', availableH + 'px');

    document.body.style.paddingTop = `${topOffset}px`;

    const mobileMenu = document.getElementById('mobileMenu');
    if (mobileMenu) {
        mobileMenu.style.top = `${topOffset}px`;
        mobileMenu.style.height = `${availableH}px`;
    }

    const cartDrawer = document.getElementById('cartDrawer');
    const cartOverlay = document.getElementById('cartOverlay');

    if (cartDrawer) {
        cartDrawer.style.top = `${topOffset}px`;
        cartDrawer.style.height = `${availableH}px`;
        cartDrawer.style.maxHeight = `${availableH}px`;
    }

    if (cartOverlay) {
        cartOverlay.style.top = `${topOffset}px`;
        cartOverlay.style.height = `${availableH}px`;
    }
}

window.addEventListener('resize', () => {
    setTimeout(updateHeights, 50);
});

window.addEventListener('orientationchange', () => {
    setTimeout(updateHeights, 250);
});

if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', () => {
        setTimeout(updateHeights, 50);
    });

    window.visualViewport.addEventListener('scroll', () => {
        setTimeout(updateHeights, 50);
    });
}

// ===== EVENT LISTENERS =====
document.addEventListener('DOMContentLoaded', function () {
    // Update heights dynamically
    updateHeights();

    // Load cart from localStorage
    loadCartFromLocalStorage();

    // Mobile menu functionality
    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
    if (mobileMenuToggle) {
        mobileMenuToggle.addEventListener('click', toggleMobileMenu);
    }

    const mobileMenuLinks = document.querySelectorAll('.mobile-nav a');
    mobileMenuLinks.forEach(link => {
        link.addEventListener('click', () => {
            const mobileMenu = document.getElementById('mobileMenu');
            const toggleButton = document.querySelector('.mobile-menu-toggle');

            if (mobileMenu) mobileMenu.classList.remove('active');
            if (toggleButton) toggleButton.classList.remove('active');
            document.body.style.overflow = '';
        });
    });

    // Cart drawer functionality
    const cartButton = document.getElementById('cartButton');
    const closeCartButton = document.getElementById('closeCart');
    const cartOverlay = document.getElementById('cartOverlay');
    const checkoutButton = document.getElementById('checkoutButton');

    if (cartButton) cartButton.addEventListener('click', openCartDrawer);
    if (closeCartButton) closeCartButton.addEventListener('click', closeCartDrawer);
    if (cartOverlay) cartOverlay.addEventListener('click', closeCartDrawer);
    if (checkoutButton) checkoutButton.addEventListener('click', proceedToCheckout);

    // Escape key to close cart drawer
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
            closeCartDrawer();
        }
    });

    // Handle viewport changes (iPhone Safari address bar show/hide)
    let viewportHeight = window.innerHeight;
    window.addEventListener('resize', function() {
        if (window.innerHeight !== viewportHeight) {
            viewportHeight = window.innerHeight;
            updateHeights();
            positionCheckoutButtonForMobile();
        }
    });

    // Initial positioning
    setTimeout(() => {
        updateHeights();
        positionCheckoutButtonForMobile();
    }, 300);

    // Announcement bar click functionality
    const announcementItems = document.querySelectorAll('.announcement-item');
    announcementItems.forEach(item => {
        item.addEventListener('click', function() {
            // Add a quick flash effect
            this.style.backgroundColor = 'rgba(56, 182, 204, 0.3)';
            setTimeout(() => {
                this.style.backgroundColor = '';
            }, 200);

            // Optional: Scroll to special offers or FAQ section
            const isDeliveryPromo = this.textContent.includes('FREE DELIVERY');
            const targetSection = isDeliveryPromo ? 'shipping-info' : 'discounts-info';
            const section = document.getElementById(targetSection);

            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        });
    });
});

// ===== GLOBAL EXPORTS =====
window.addToCart = addToCart;
window.addProductToCart = addProductToCart;
window.updateCartItemQuantity = updateCartItemQuantity;
window.removeCartItem = removeCartItem;
window.clearCart = clearCart;
window.openCartDrawer = openCartDrawer;
window.closeCartDrawer = closeCartDrawer;
